<script lang="ts" setup>
import ArrowIcon from 'assets/icons/arrows/arrowWithStickLeft2.svg'
import CloseIcon from 'assets/icons/close/24/close-l.svg'
import FiltersList from '~/entities/filters/FiltersList.vue'
import CatalogCategories from '~/entities/catalog/products/filters/CatalogCategories.vue';
import MobileModal from '~/components/ui/modals/MobileModal.vue'
import useCatalogStore from '~/shared/store/modules/catalog'
import AppSwitcherCheckbox from '~/components/ui/form/formElements/AppSwitcherCheckbox.vue'
import AppFilterCheckbox from '~/components/ui/form/formElements/AppFilterCheckbox.vue'
import AppButton from '~/components/ui/buttons/AppButton.vue'

import type { ISwitcher, IOptionWithCategory } from '~/shared/interfaces/ui/form/formInputs'
import type { IFilter, IFiltersList } from '~/shared/interfaces/filtersList'
import type { ICatalogFilter, ISelectedFilter, ISetSelectedFilter } from '~/shared/interfaces/catalog'


interface IFilterWithSelectIndicator extends IFilter {
  isSelected?: boolean,
}

const catalogStore = useCatalogStore()

const modalTitle = ref('Фильтры')

const showAllFiltersCategory = ref(false)
const allFiltersCategory = ref('')
const isClearFullList = ref(false)
const selectedFilterInfo = ref<ICatalogFilter | null>(null);
const isFiltersSelected = ref(false)

const setModalTitle = (title: string) => modalTitle.value = title

function toggleFilter({ category }: {category: ISetSelectedFilter}) {
  const { filterId } = category;

  if (filterId == 'brands') {
    catalogStore.setBrandsSelectedStatus(category);
  }else {
    catalogStore.setFiltersSelectedStatus(category);
  }
}

function showAllFilters(filter: ICatalogFilter) {
  allFiltersCategory.value = filter.id
  showAllFiltersCategory.value = true
  selectedFilterInfo.value = filter
  setModalTitle(filter.title)
}

function closeAllFiltersCategory() {
  showAllFiltersCategory.value = false
  selectedFilterInfo.value = null
  allFiltersCategory.value = ''
  setModalTitle('Фильтры')
}

function setSelectedFilters(selectedFilters: {
  category: string
  filters: {
    id: string
    name: string
  }[]
}, name: string) {

  const { category, filters } = selectedFilters;
  const result: ISelectedFilter = {
    filterId: category,
    filterName: name,
    values: filters.map(filter => {
      return {
        name: filter.name,
        value: filter.id,
      }
    })
  }

  if (category == 'brands') {
    catalogStore.replaceSelectedBrandList(result);
  } else {
    catalogStore.setSelectedFilter(result);
  }
}

function clearAllFilters() {
  isClearFullList.value = !isClearFullList.value
  catalogStore.resetSelectedList()
  isFiltersSelected.value = false
}

// function clearFullList() {
//   isClearFullList.value = !isClearFullList.value
// }

watch(() => catalogStore.selectedFilters, () => {
  isFiltersSelected.value = !!catalogStore.selectedFilters.filters.length || !!catalogStore.selectedFilters.brands.length
}, {deep: true})
</script>

<template>
  <MobileModal
    :modal-is-open="catalogStore.showFiltersModal"
    :full-page="true"
    :has-scroll="true"
    :hide-close="showAllFiltersCategory"
    @close-modal="catalogStore.setFiltersModalStatus(false)"
  >
    <div class="filter-modal">
      <div class="filter-modal__header">
        <div
          v-if="showAllFiltersCategory"
          class="filter-modal__back"
          @click="closeAllFiltersCategory"
        >
          <ArrowIcon class="filter-modal__back-icon"/>
        </div>
        <div
          v-else
          class="filter-modal__close"
          @click="catalogStore.setFiltersModalStatus(false)"
        >
          <CloseIcon/>
        </div>
        <div class="filter-modal__title">{{ modalTitle }}</div>
        <AppButton
          v-if="isFiltersSelected"
          text="Сбросить"
          type="ghost"
          sub-type="primary"
          :font-weight="400"
          class="filter-modal__clear-filters"
          @click="clearAllFilters"
        />
      </div>
      <div
        class="filter-modal__main"
        v-if="!showAllFiltersCategory"
      >
        <CatalogCategories
          :is-without-title="true"
          @click-link="catalogStore.setFiltersModalStatus(false)"
        />
        <div class="filter-modal__main-separator"></div>
        <div
          v-for="filter in catalogStore.filters"
          class="filter-modal__item"
        >
          <div
            class="filter-modal__item-top">
            <div class="filter-modal__item-name">{{ filter.title }}</div>
            <button
              class="filter-modal__item-all"
              v-if="filter.values.length > 10"
              @click="showAllFilters(filter)"
            >
              Все
            </button>
          </div>
          <div
            class="filter-modal__item-container"
            :class="filter.id === 'brands' && 'brands'"
          >
            <template
              v-for="(filterValue, index) in filter.values"
              :key="`${filter.id}_${filterValue.id}`"
            >
              <AppFilterCheckbox
                v-show="index < 10"
                :id="filterValue.value"
                :name="filterValue.title || filterValue.value"
                :is-selected="filterValue.active"
                :category="{
                  filterId: filter.id,
                  filterName: filter.title,
                  value: filterValue.value,
                  name: filterValue.title || filterValue.value,
                  active: !filterValue.active,
                }"
                @select-value="toggleFilter"
              />
            </template>
          </div>
        </div>
      </div>
      <div
        class="filter-modal__footer"
        v-if="!showAllFiltersCategory"
      >
        <AppButton
          text="Показать товары"
          @click="catalogStore.setFiltersModalStatus(false)"
        />
      </div>
      <Transition>
        <div
          class="filter-modal__all"
          v-if="showAllFiltersCategory && selectedFilterInfo"
        >
          <FiltersList
            :is-modal-list="true"
            search-placeholder="Найти"
            :has-search="true"
            :has-apply="true"
            :is-clear-list="isClearFullList"
            :options="selectedFilterInfo.values.map(({ value, title }) => {
              return {
                id: value,
                name: title || value
              }
            })"
            :priority-selected-options="selectedFilterInfo.values.reduce<IFilter[]>((acc, item) => {
              const { value, title, active } = item;
              if (active) {
                acc.push({
                  id: value,
                  name: title || value
                });
              }

              return acc;
            }, [])"
            :name="selectedFilterInfo.title"
            :value="allFiltersCategory"
            @set-options-activity="setSelectedFilters($event, selectedFilterInfo?.title || '')"
            @close-all-filter-modal="closeAllFiltersCategory"
          />
        </div>
      </Transition>
    </div>
  </MobileModal>
</template>

<style lang="scss" scoped>
:deep(.mobile-modal__content-wrapper) {
  overflow: visible;
  overflow-y: auto;
}

.v-enter-active,
.v-leave-active {
  transition: opacity .3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.categories {
  padding: 12px 0 16px;
  position: relative;

  :deep(.categories-list__link) {
    @include font(16px, 20px);
  }
}

.filter-checkbox {
  &.active {
    order: -1;
  }
}

.filter-modal {
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 1000;

  &__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-right: 16px;
  }

  &__close {
    position: absolute;
    left: 0;
    height: 24px;
    top: 0;
  }

  &__clear-filters {
    position: absolute;
    right: 16px;
    top: 0;
  }

  &__title {
    @include font(20px, 24px, 600);
  }

  &__back {
    position: absolute;
    left: 0;
    top: 0;
  }

  &__main {
    display: flex;
    flex-direction: column;
    padding-right: 16px;

    &-separator {
      width: 100%;
      height: 1px;
      background-color: $bg_border;
      flex-shrink: 0;
      margin: 4px 0;
    }
  }

  &__item {
    margin-top: 28px;

    &:nth-child(3) {
      margin-top: 12px;
    }

    &-all {
      background-color: transparent;
      @include font(14px, 16px);
      color: $content_orange;
    }

    &-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &-container {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 16px;

      &.brands {
        text-transform: uppercase;
      }
    }

    &-name {
      color: $content_primary;
      @include font(16px, 20px, 500);
    }
  }

  &__main {
    height: calc(100% - 152px);
    overflow-y: auto;
    scrollbar-color: $content_secondary;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      &-thumb {
        background-color: red;
        border-radius: 4px;
      }
    }
  }

  &__footer {
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-right: 16px;

    .button {
      width: 100%;
      margin: 20px 0 24px;
    }
  }

  &__chosen {
    display: flex;
    gap: 8px 4px;
    flex-wrap: wrap;
    padding-bottom: 8px;

    &-clear,
    &-item {
      padding: 8px 12px;
      border-radius: 20px;
      @include font(14px, 16px);
    }

    &-clear {
      background-color: $bg_light-grey;
    }

    &-item {
      display: flex;
      background-color: $bg_black;
      align-items: center;
      gap: 10px;
      color: $content_white;
    }
  }

  &__all {
    position: absolute;
    top: 64px;
    left: 0;
    width: 100%;
    background-color: $bg_page;
    z-index: 10;
    height: 100%;
  }
}
</style>
